import { getApiBaseUrl } from '../../../shared/utils/apiBase';

/**
 * Resolves role access from the current server session (cookie-authenticated).
 * Returns null only when the lookup cannot be trusted (network/server issue).
 */
export async function resolveSessionRoleAccessMap(
  roleNames: string[],
  configSchema: string,
): Promise<Record<string, boolean> | null> {
  try {
    const apiBase = getApiBaseUrl();
    const response = await fetch(`${apiBase}/api/userRoleNames`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      return null;
    }

    const userRoleNamesResponse = await response.json();
    const roleSet = extractRoleNamesFromSession(userRoleNamesResponse, configSchema);

    return roleNames.reduce(
      (acc, roleName) => {
        acc[roleName] = roleSet.has(roleName.toLowerCase());
        return acc;
      },
      {} as Record<string, boolean>,
    );
  } catch {
    return null;
  }
}

function extractRoleNamesFromSession(payload: unknown, configSchema: string): Set<string> {
  if (!payload || typeof payload !== 'object') {
    return new Set();
  }

  const users = (payload as Record<string, unknown>)[`${configSchema}_mbd_users`];
  if (!Array.isArray(users)) {
    return new Set();
  }

  const sessionUser = users[0] as Record<string, unknown> | undefined;
  const roleMappings = sessionUser?.['mbdUserToRolesByUserId'];
  if (!Array.isArray(roleMappings)) {
    return new Set();
  }

  return new Set(
    roleMappings.flatMap((mapping) => {
      if (!mapping || typeof mapping !== 'object') {
        return [];
      }

      const roleNode = (mapping as Record<string, unknown>)['mbd_role'];
      const roleName =
        roleNode && typeof roleNode === 'object'
          ? (roleNode as Record<string, unknown>)['role_name']
          : null;

      return typeof roleName === 'string' ? [roleName.toLowerCase()] : [];
    }),
  );
}
